/**
 * Transaction process graph for deferred product orders:
 *   - deferred-purchase
 *
 * This process allows buyers to express interest in a product WITHOUT requiring
 * the seller to have Stripe connected upfront. The payment is deferred until
 * after the seller accepts the order.
 *
 * Flow:
 * 1. Buyer expresses interest (no payment required)
 * 2. Seller receives notification and can connect Stripe if needed
 * 3. Seller accepts the order
 * 4. Buyer receives notification to complete payment
 * 5. Buyer makes payment
 * 6. Standard purchase flow continues
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer expresses interest in purchasing a listing,
  // a transaction is created WITHOUT payment. This allows sellers
  // who don't have Stripe connected yet to still receive orders.
  REQUEST_ORDER: 'transition/request-order',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with an order request.
  INQUIRE: 'transition/inquire',
  REQUEST_ORDER_AFTER_INQUIRY: 'transition/request-order-after-inquiry',

  // Seller can accept the order (must have Stripe connected at this point)
  ACCEPT_ORDER: 'transition/accept-order',

  // Seller can decline the order
  DECLINE_ORDER: 'transition/decline-order',

  // Customer can cancel their order request before seller responds
  CANCEL_ORDER: 'transition/cancel-order',

  // Order expires if seller doesn't respond in time
  EXPIRE_ORDER: 'transition/expire-order',

  // After seller accepts, customer initiates payment
  // At this transition a PaymentIntent is created by Marketplace API.
  REQUEST_PAYMENT: 'transition/request-payment',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // If buyer doesn't pay within time limit after seller accepts
  EXPIRE_AWAITING_PAYMENT: 'transition/expire-awaiting-payment',

  // After payment confirmed, seller can accept (capture payment) or decline (refund)
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',

  // Order expires if not handled in time
  EXPIRE: 'transition/expire',

  // Customer can cancel after purchase
  CANCEL: 'transition/cancel',

  // Shipping transitions
  MARK_SHIPPED_BY_PROVIDER: 'transition/mark-shipped-by-provider',
  MARK_SHIPPED_BY_OPERATOR: 'transition/mark-shipped-by-operator',
  CANCEL_BY_CUSTOMER: 'transition/cancel-by-customer',
  EXPIRE_SHIPPING_LABEL: 'transition/expire-shipping-label',
  UPDATE_SHIPPING_INFO: 'transition/update-shipping-info',

  // Delivery transitions
  MARK_DELIVERED_BY_OPERATOR: 'transition/mark-delivered-by-operator',
  ITEM_RECIEVED_BY_CUSTOMER: 'transition/item-recieved-by-customer',

  // Completion transitions
  COMPLETE_SUCCESSFULLY: 'transition/complete-succesfully',
  ISSUE_RAISED_BY_CUSTOMER: 'transition/issue-raised-by-customer',
  COMPLETE_AUTOMATICALLY: 'transition/complete-automatically',
  COMPLETE_AFTER_ISSUE_RESOLVED: 'transition/complete-by-operator-after-dispute',

  // Reviews are given through transaction transitions.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_ACCEPTANCE: 'pending-acceptance',
  ACCEPTED_AWAITING_PAYMENT: 'accepted-awaiting-payment',
  DECLINED: 'declined',
  CANCELLED: 'cancelled',
  ORDER_EXPIRED: 'order-expired',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PURCHASED: 'purchased',
  ACCEPTED: 'accepted',
  SHIPPED: 'shipped',
  DELIVERED: 'delivered',
  ISSUE: 'issue',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  EXPIRE: 'expire',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'deferred-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_ORDER]: states.PENDING_ACCEPTANCE,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_ORDER_AFTER_INQUIRY]: states.PENDING_ACCEPTANCE,
      },
    },

    // Buyer has expressed interest, waiting for seller to respond
    [states.PENDING_ACCEPTANCE]: {
      on: {
        [transitions.ACCEPT_ORDER]: states.ACCEPTED_AWAITING_PAYMENT,
        [transitions.DECLINE_ORDER]: states.DECLINED,
        [transitions.CANCEL_ORDER]: states.CANCELLED,
        [transitions.EXPIRE_ORDER]: states.ORDER_EXPIRED,
      },
    },

    // Terminal states for order request phase
    [states.DECLINED]: {},
    [states.ORDER_EXPIRED]: {},

    // Seller has accepted, waiting for buyer to pay
    [states.ACCEPTED_AWAITING_PAYMENT]: {
      on: {
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
        [transitions.EXPIRE_AWAITING_PAYMENT]: states.PAYMENT_EXPIRED,
      },
    },

    // Payment phase
    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.CANCELLED]: {},

    // Standard purchase flow after payment
    [states.PURCHASED]: {
      on: {
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRE,
        [transitions.CANCEL]: states.CANCELLED,
      },
    },

    [states.ACCEPTED]: {
      on: {
        [transitions.MARK_SHIPPED_BY_OPERATOR]: states.SHIPPED,
        [transitions.MARK_SHIPPED_BY_PROVIDER]: states.SHIPPED,
        [transitions.CANCEL_BY_CUSTOMER]: states.CANCELLED,
        [transitions.EXPIRE_SHIPPING_LABEL]: states.CANCELLED,
      },
    },

    [states.SHIPPED]: {
      on: {
        [transitions.MARK_DELIVERED_BY_OPERATOR]: states.DELIVERED,
        [transitions.ITEM_RECIEVED_BY_CUSTOMER]: states.DELIVERED,
        [transitions.UPDATE_SHIPPING_INFO]: states.SHIPPED,
      },
    },

    [states.DELIVERED]: {
      on: {
        [transitions.COMPLETE_SUCCESSFULLY]: states.COMPLETED,
        [transitions.ISSUE_RAISED_BY_CUSTOMER]: states.ISSUE,
        [transitions.COMPLETE_AUTOMATICALLY]: states.COMPLETED,
      },
    },

    [states.ISSUE]: {
      on: {
        [transitions.COMPLETE_AFTER_ISSUE_RESOLVED]: states.COMPLETED,
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
    [states.EXPIRE]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.REQUEST_ORDER,
    transitions.REQUEST_ORDER_AFTER_INQUIRY,
    transitions.ACCEPT_ORDER,
    transitions.DECLINE_ORDER,
    transitions.CANCEL_ORDER,
    transitions.CONFIRM_PAYMENT,
    transitions.ACCEPT,
    transitions.DECLINE,
    transitions.CANCEL,
    transitions.MARK_SHIPPED_BY_PROVIDER,
    transitions.MARK_SHIPPED_BY_OPERATOR,
    transitions.MARK_DELIVERED_BY_OPERATOR,
    transitions.ITEM_RECIEVED_BY_CUSTOMER,
    transitions.COMPLETE_SUCCESSFULLY,
    transitions.COMPLETE_AUTOMATICALLY,
    transitions.ISSUE_RAISED_BY_CUSTOMER,
    transitions.COMPLETE_AFTER_ISSUE_RESOLVED,
    transitions.CANCEL_BY_CUSTOMER,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE,
    transitions.EXPIRE_ORDER,
    transitions.UPDATE_SHIPPING_INFO,
  ].includes(transition);
};

export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  // REQUEST_ORDER sets line items (product price, no shipping yet)
  // REQUEST_PAYMENT sets line items including shipping
  return [
    transitions.REQUEST_ORDER,
    transitions.REQUEST_ORDER_AFTER_INQUIRY,
    transitions.REQUEST_PAYMENT,
  ].includes(transition);
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE_AUTOMATICALLY,
    transitions.COMPLETE_SUCCESSFULLY,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.DECLINE,
    transitions.CANCEL,
    transitions.CANCEL_BY_CUSTOMER,
    transitions.EXPIRE,
    transitions.EXPIRE_SHIPPING_LABEL,
  ];
  return txRefundedTransitions.includes(transition);
};

// Check when transaction is cancelled before payment (no refund needed)
export const isCancelledBeforePayment = transition => {
  const txCancelledBeforePaymentTransitions = [
    transitions.DECLINE_ORDER,
    transitions.CANCEL_ORDER,
    transitions.EXPIRE_ORDER,
    transitions.EXPIRE_AWAITING_PAYMENT,
  ];
  return txCancelledBeforePaymentTransitions.includes(transition);
};

// States where the provider needs to take action
export const statesNeedingProviderAttention = [states.PENDING_ACCEPTANCE, states.PURCHASED];

// States where the customer needs to take action
export const statesNeedingCustomerAttention = [states.ACCEPTED_AWAITING_PAYMENT];

